import { z } from "zod";
import { CreateOrderRequestSchema } from "../api/orders.schema";

// =========================================================================
// BASE EVENT SCHEMA (Común para todos los eventos)
// =========================================================================

export const BaseEventSchema = z.object({
  event_id: z.uuid(),
  event_type: z.string(),
  timestamp: z.iso.datetime(),
  trace_id: z.string(),
  version: z.string().default("1.0"),
});

// =========================================================================
// EVENTO: order-created (Topic: order-topic)
// =========================================================================

/**
 * El Worker recibe todo lo que mandó el cliente + IDs internos resueltos por la API
 */
export const OrderCreatedEventSchema = BaseEventSchema.extend({
  event_type: z.literal("order.created"),

  // Datos enriquecidos por el Backend antes de publicar
  internal_shop_id: z.string(),
  internal_country_id: z.string(),
  internal_order_id: z.string(), // UUID generado por el backend

  // Payload original del cliente
  payload: CreateOrderRequestSchema,

  // Origen del pedido: 'direct' (pago inmediato) o 'lead' (abandonment/upsell)
  source: z.enum(["direct", "lead"]).default("direct"),
});

// =========================================================================
// EVENTO: order-fraud-check-completed
// =========================================================================

export const FraudCheckResultEnum = z.enum([
  "approved",
  "rejected",
  "manual_review",
]);

export const OrderFraudCheckCompletedEventSchema = BaseEventSchema.extend({
  event_type: z.literal("order.fraud_check_completed"),

  order_id: z.string(),
  shop_id: z.string(),

  fraud_check: z.object({
    result: FraudCheckResultEnum,
    score: z.number().min(0).max(100),
    provider: z.enum(["internal", "dropi", "mix"]),
    reason: z.string().optional(),
    details: z.record(z.string(), z.any()).optional(),
  }),
});

// =========================================================================
// EVENTO: order-draft-created
// =========================================================================

export const OrderDraftCreatedEventSchema = BaseEventSchema.extend({
  event_type: z.literal("order.draft_created"),

  order_id: z.string(),
  shop_id: z.string(),

  shopify_draft_order: z.object({
    id: z.string(),
    admin_url: z.string(),
    invoice_url: z.string().optional(),
    total: z.number().nonnegative(),
  }),
});

// =========================================================================
// EVENTO: order-upsell-added
// =========================================================================

export const OrderUpsellAddedEventSchema = BaseEventSchema.extend({
  event_type: z.literal("order.upsell_added"),

  order_id: z.string(),
  shop_id: z.string(),

  upsell: z.object({
    offer_id: z.string().min(1),
    variant_id: z.string().min(1),
    quantity: z.number().int().positive(),
    added_value: z.number().nonnegative(),
  }),

  updated_total: z.number().nonnegative(),
});

// =========================================================================
// EVENTO: order-finalized
// =========================================================================

export const OrderFinalizedEventSchema = BaseEventSchema.extend({
  event_type: z.literal("order.finalized"),

  order_id: z.string(),
  shop_id: z.string(),

  finalization: z.object({
    final_total: z.number().nonnegative(),
    upsells_accepted: z.number().int().nonnegative(),
    completion_time_ms: z.number().nonnegative(),
  }),
});

// =========================================================================
// EVENTO: order-failed
// =========================================================================

export const OrderFailureReasonEnum = z.enum([
  "NO_COVERAGE",
  "FRAUD_REJECTED",
  "SHOPIFY_API_ERROR",
  "PAYMENT_GATEWAY_ERROR",
  "INVENTORY_UNAVAILABLE",
  "SYSTEM_ERROR",
]);

export const OrderFailedEventSchema = BaseEventSchema.extend({
  event_type: z.literal("order.failed"),

  order_id: z.string(),
  shop_id: z.string(),

  failure: z.object({
    reason: OrderFailureReasonEnum,
    message: z.string(),
    retry_count: z.number().int().nonnegative(),
    is_retryable: z.boolean(),
    error_details: z.record(z.string(), z.any()).optional(),
  }),
});

// =========================================================================
// UNION TYPE - Todos los eventos posibles
// =========================================================================

// =========================================================================
// EVENTO: product-sync
// =========================================================================

export const ProductSyncEventSchema = BaseEventSchema.extend({
  event_type: z.literal("product.sync"),
  shop_domain: z.string(),
  cursor: z.string().optional(),
});

// =========================================================================
// UNION TYPE - Todos los eventos posibles
// =========================================================================

export const PubSubEventSchema = z.discriminatedUnion("event_type", [
  OrderCreatedEventSchema,
  OrderFraudCheckCompletedEventSchema,
  OrderDraftCreatedEventSchema,
  OrderUpsellAddedEventSchema,
  OrderFinalizedEventSchema,
  OrderFailedEventSchema,
  ProductSyncEventSchema,
]);

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type BaseEvent = z.infer<typeof BaseEventSchema>;
export type OrderCreatedEvent = z.infer<typeof OrderCreatedEventSchema>;
export type OrderFraudCheckCompletedEvent = z.infer<
  typeof OrderFraudCheckCompletedEventSchema
>;
export type OrderDraftCreatedEvent = z.infer<
  typeof OrderDraftCreatedEventSchema
>;
export type OrderUpsellAddedEvent = z.infer<typeof OrderUpsellAddedEventSchema>;
export type OrderFinalizedEvent = z.infer<typeof OrderFinalizedEventSchema>;
export type OrderFailedEvent = z.infer<typeof OrderFailedEventSchema>;
export type ProductSyncEvent = z.infer<typeof ProductSyncEventSchema>;
export type PubSubEvent = z.infer<typeof PubSubEventSchema>;
